import type { User } from "firebase/auth";
import { resolveGmAccess } from "@/auth/gm-access";
import { ensureGmClaimsSynced, readGmClaims, syncGmClaimsNow } from "@/auth/sync-gm-claims";
import { signOutUser } from "@/auth/google-auth";
import { bindGmSettingsSection, renderGmSettingsSectionHtml } from "@/pages/gm-settings-section";
import { bindGmUsersSection, renderGmUsersSectionHtml } from "@/pages/gm-users-section";
import { hydrateGmMonsterCatalog } from "@/gm/gm-monster-catalog";
import { hydrateGmPartCatalog } from "@/gm/gm-part-catalog";
import { bindGmMonstersSection, renderGmMonstersSectionHtml } from "@/pages/gm-monsters-section";
import {
  bindGmTowerSection,
  prepareGmTowerSection,
  renderGmTowerSectionHtml,
} from "@/pages/gm-tower-section";
import {
  bindGmPartsSection,
  prepareGmPartsSection,
  renderGmPartsSectionHtml,
} from "@/pages/gm-parts-section";
import {
  bindGmShopSection,
  prepareGmShopSection,
  renderGmShopSectionHtml,
} from "@/pages/gm-shop-section";
import {
  bindGmRankBadgesSection,
  prepareGmRankBadgesSection,
  renderGmRankBadgesSectionHtml,
} from "@/pages/gm-rank-badges-section";
import {
  bindGmControlSection,
  renderGmControlSectionHtml,
} from "@/pages/gm-control-section";
import {
  bindGmDungeonShopSection,
  prepareGmDungeonShopSection,
  renderGmDungeonShopSectionHtml,
} from "@/pages/gm-dungeon-shop-section";
import {
  bindGmHomeRailSection,
  prepareGmHomeRailSection,
  renderGmHomeRailSectionHtml,
} from "@/pages/gm-home-rail-section";
import { hydrateGmShopCatalog } from "@/gm/gm-shop-catalog";
import {
  gmPathForSection,
  normalizeAppPath,
  parseGmSectionFromPath,
  type GmSection,
} from "@/pages/gm-routes";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const SECTION_META: Record<
  GmSection,
  { label: string; title: string; subtitle: string; icon: string; hint: string; ready: boolean }
> = {
  overview: {
    label: "ภาพรวม",
    title: "ภาพรวม",
    subtitle: "สถานะสิทธิ์และทางลัดไปงานที่ใช้บ่อย",
    icon: "◈",
    hint: "สถานะ",
    ready: true,
  },
  settings: {
    label: "ค่าพื้นฐาน",
    title: "ค่าพื้นฐานเกม",
    subtitle: "สเตตผู้เล่น · ตีธรรมดา · มอนสเตอร์ · สูตรต่อสู้",
    icon: "⚙",
    hint: "บาลานซ์",
    ready: true,
  },
  monsters: {
    label: "มอนสเตอร์",
    title: "มอนสเตอร์",
    subtitle: "อัปโหลดสไปรต์ชีต · ตั้งค่าพลัง · ใส่คลังบ้าน",
    icon: "⚔",
    hint: "สร้าง",
    ready: true,
  },
  parts: {
    label: "ชิ้นส่วน",
    title: "ชิ้นส่วนฉาก",
    subtitle: "อัปโหลดบ้าน ตกแต่ง พื้น ฉาก · แยกหมวด · ใช้แต่งบ้าน",
    icon: "▦",
    hint: "ฉาก",
    ready: true,
  },
  dungeonShop: {
    label: "ร้านด่าน",
    title: "ร้านด่าน (Dungeon)",
    subtitle: "ราคาทอง/Cash · เพดานซื้อมอนสเตอร์และคูณพลัง",
    icon: "⚔",
    hint: "Dungeon",
    ready: true,
  },
  shop: {
    label: "ร้าน",
    title: "สินค้าร้าน",
    subtitle: "รูป ราคา ลดราคา เปิด/ปิดขาย · ทอง เพชร Cash",
    icon: "🛒",
    hint: "ร้าน",
    ready: true,
  },
  tower: {
    label: "หอคอย",
    title: "หอ 100 ชั้น",
    subtitle: "รายการชั้น 1–100 · สร้าง/แก้ไขฉากเปิดแท็บใหม่ (ฉากเป็นหลัก)",
    icon: "▲",
    hint: "เวฟ",
    ready: true,
  },
  users: {
    label: "ผู้ใช้",
    title: "จัดการผู้ใช้",
    subtitle: "รายชื่อผู้เล่น · รีเซ็ตเซฟคลาวด์ · แบน/ปลดแบน",
    icon: "◎",
    hint: "บัญชี",
    ready: true,
  },
  rankBadges: {
    label: "ป้ายแรงค์",
    title: "ป้ายแรงค์ & บัฟ",
    subtitle: "อัปโหลดป้าย 2 เฟรม · ตั้งบัฟต่อบอร์ด",
    icon: "★",
    hint: "Rank",
    ready: true,
  },
  homeRail: {
    label: "เมนูบ้าน",
    title: "เมนูซ้ายหน้าบ้าน",
    subtitle: "เรียงลำดับ · คอลัมน์ · เลือกเมนูที่แสดง",
    icon: "☰",
    hint: "Rail",
    ready: true,
  },
  control: {
    label: "ควบคุม",
    title: "ควบคุมระบบ",
    subtitle: "บังคับคำนวณ Rank และเครื่องมือ owner อื่น (ต่อยอด)",
    icon: "⎈",
    hint: "Control",
    ready: true,
  },
  events: {
    label: "Event",
    title: "Event & ฉาก",
    subtitle: "ช่วงเวลา โบนัส และฉากกิจกรรม",
    icon: "✦",
    hint: "เร็วๆ นี้",
    ready: false,
  },
  drops: {
    label: "Drop",
    title: "ตาราง Drop",
    subtitle: "น้ำหนัก loot และ pity (สุ่มฝั่ง server)",
    icon: "◇",
    hint: "เร็วๆ นี้",
    ready: false,
  },
};

const PRIMARY_SECTIONS: GmSection[] = [
  "overview",
  "settings",
  "monsters",
  "parts",
  "shop",
  "tower",
  "users",
  "rankBadges",
  "homeRail",
  "control",
];
const LATER_SECTIONS: GmSection[] = ["events", "drops"];

function navLink(id: GmSection, active: GmSection): string {
  const meta = SECTION_META[id];
  const activeClass = id === active ? " gm-nav-item--active" : "";
  const soon = meta.ready ? "" : " gm-nav-item--soon";
  const href = gmPathForSection(id);
  return `
    <a
      href="${href}"
      class="gm-nav-item${activeClass}${soon}"
      data-section="${id}"
      ${id === active ? 'aria-current="page"' : ""}
      ${meta.ready ? "" : 'aria-disabled="true"'}
    >
      <span class="gm-nav-icon" aria-hidden="true">${meta.icon}</span>
      <span class="gm-nav-copy">
        <span class="gm-nav-label">${meta.label}</span>
        <span class="gm-nav-hint">${meta.hint}</span>
      </span>
    </a>
  `;
}

export async function mountGmPage(root: HTMLElement, user: User): Promise<() => void> {
  root.className = "page-shell page-gm";
  root.innerHTML = `
    <main class="gm-loading" aria-busy="true">
      <div class="gm-loading-card">
        <span class="gm-loading-mark" aria-hidden="true">GM</span>
        <p>กำลังตรวจสอบสิทธิ์ owner…</p>
      </div>
    </main>
  `;

  const cachedClaims = await readGmClaims(user);
  const preAccess = resolveGmAccess(user, cachedClaims);
  if (!preAccess.canAccessGmConsole) {
    const signedInEmail = escapeHtml(user.email ?? "(ไม่มีอีเมลในโปรไฟล์)");
    root.innerHTML = `
      <main class="auth-card gm-forbidden" aria-labelledby="gm-forbidden-title">
        <p class="gm-kicker">GM Console</p>
        <h1 id="gm-forbidden-title">ไม่มีสิทธิ์เข้าคอนโซล</h1>
        <p class="hint">เฉพาะอีเมล owner (<code>VITE_GM_OWNER_EMAIL</code> หรือ claim <code>owner</code>)</p>
        <p class="hint">ล็อกอินด้วย: <strong>${signedInEmail}</strong></p>
        <p class="hint"><code>VITE_GM_ALLOW_ALL</code> เปิดโหมด GM ในเกมได้ แต่<strong>ไม่</strong>เปิดหน้านี้</p>
        <div class="home-actions">
          <a class="btn btn-primary" href="/home?character=1">กลับไปตัวละคร</a>
        </div>
      </main>
    `;
    return () => {};
  }

  const claims = await ensureGmClaimsSynced(user);
  const access = resolveGmAccess(user, claims);
  await hydrateGmMonsterCatalog();
  await hydrateGmPartCatalog();
  await hydrateGmShopCatalog();
  await prepareGmTowerSection();
  await prepareGmPartsSection();
  await prepareGmHomeRailSection();
  await prepareGmShopSection();

  let section: GmSection = parseGmSectionFromPath(window.location.pathname);
  const canonicalPath = gmPathForSection(section);
  if (normalizeAppPath(window.location.pathname) !== canonicalPath) {
    history.replaceState({ gmSection: section }, "", canonicalPath);
  }
  let unbindSettings: (() => void) | null = null;
  let unbindUsers: (() => void) | null = null;
  let unbindMonsters: (() => void) | null = null;
  let unbindParts: (() => void) | null = null;
  let unbindTower: (() => void) | null = null;
  let unbindShop: (() => void) | null = null;
  let unbindControl: (() => void) | null = null;
  let unbindRankBadges: (() => void) | null = null;
  let unbindHomeRail: (() => void) | null = null;
  let rankBadgeDrafts = await prepareGmRankBadgesSection();
  let dungeonShopDraft = await prepareGmDungeonShopSection();
  const email = user.email ?? "—";

  const renderSectionBody = (): string => {
    const verified = access.verifiedByClaims
      ? `<span class="gm-badge gm-badge--ok">claims ยืนยันแล้ว</span>`
      : `<span class="gm-badge gm-badge--warn">allowlist client — ยังไม่ยืนยัน server</span>`;
    const claimsSyncBlock = access.verifiedByClaims
      ? ""
      : `
          <p class="hint">บันทึกป้ายแรงค์ / ร้าน dungeon ต้องมี claim <code>owner</code> จาก Cloud Functions</p>
          <div class="home-actions">
            <button type="button" class="btn btn-secondary" id="gm-sync-claims-btn">ซิงค์สิทธิ์กับ server</button>
          </div>
          <p class="hint" id="gm-sync-claims-status" role="status"></p>
        `;

    if (section === "overview") {
      const shortcuts = PRIMARY_SECTIONS.filter((id) => id !== "overview")
        .map((id) => {
          const meta = SECTION_META[id];
          return `
            <a href="${gmPathForSection(id)}" class="gm-shortcut" data-section="${id}">
              <span class="gm-shortcut-icon" aria-hidden="true">${meta.icon}</span>
              <span class="gm-shortcut-body">
                <strong>${meta.label}</strong>
                <span>${meta.subtitle}</span>
              </span>
              <span class="gm-shortcut-go" aria-hidden="true">→</span>
            </a>
          `;
        })
        .join("");

      return `
        <section class="gm-panel gm-panel--hero">
          <div class="gm-panel-head">
            <div>
              <p class="gm-kicker">Owner session</p>
              <h2 class="gm-panel-title">พร้อมจัดการเกม</h2>
            </div>
            ${verified}
          </div>
          ${claimsSyncBlock}
          <dl class="gm-meta-grid">
            <div>
              <dt>อีเมล</dt>
              <dd>${escapeHtml(email)}</dd>
            </div>
            <div>
              <dt>โหมดในเกม</dt>
              <dd><a href="/home">เปิดบ้าน / สลับ GM</a></dd>
            </div>
            <div>
              <dt>ข้อมูล</dt>
              <dd>เครื่อง + Firestore (sync หลังล็อกอิน)</dd>
            </div>
          </dl>
        </section>

        <section class="gm-panel">
          <div class="gm-panel-head">
            <h2 class="gm-panel-title">ทางลัดที่ใช้บ่อย</h2>
            <p class="gm-panel-note">กดแล้วไปหน้างานทันที</p>
          </div>
          <div class="gm-shortcut-list">${shortcuts}</div>
        </section>

        <section class="gm-panel gm-panel--muted">
          <div class="gm-panel-head">
            <h2 class="gm-panel-title">กำลังมา</h2>
          </div>
          <div class="gm-cards">
            <article class="gm-card gm-card--muted">
              <h3 class="gm-card-title">Event / ฉาก</h3>
              <p class="gm-card-desc">กำหนดช่วงเวลา โบนัส และผูกฉาก</p>
            </article>
            <article class="gm-card gm-card--muted">
              <h3 class="gm-card-title">Drop table</h3>
              <p class="gm-card-desc">สุ่ม loot ฝั่ง server + pity</p>
            </article>
          </div>
        </section>
      `;
    }
    if (section === "settings") {
      return `<div class="gm-panel gm-panel--work">${renderGmSettingsSectionHtml()}</div>`;
    }
    if (section === "monsters") {
      return `<div class="gm-panel gm-panel--work">${renderGmMonstersSectionHtml()}</div>`;
    }
    if (section === "parts") {
      return `<div class="gm-panel gm-panel--work">${renderGmPartsSectionHtml()}</div>`;
    }
    if (section === "shop") {
      return `<div class="gm-panel gm-panel--work">${renderGmShopSectionHtml()}</div>`;
    }
    if (section === "tower") {
      return `<div class="gm-panel gm-panel--work">${renderGmTowerSectionHtml()}</div>`;
    }
    if (section === "users") {
      return `<div class="gm-panel gm-panel--work">${renderGmUsersSectionHtml()}</div>`;
    }
    if (section === "rankBadges") {
      return `<div class="gm-panel gm-panel--work">${renderGmRankBadgesSectionHtml()}</div>`;
    }
    if (section === "dungeonShop") {
      return `<div class="gm-panel gm-panel--work">${renderGmDungeonShopSectionHtml()}</div>`;
    }
    if (section === "homeRail") {
      return `<div class="gm-panel gm-panel--work">${renderGmHomeRailSectionHtml()}</div>`;
    }
    if (section === "control") {
      return `<div class="gm-panel gm-panel--work">${renderGmControlSectionHtml()}</div>`;
    }
    if (section === "events") {
      return `
        <div class="gm-empty-state">
          <span class="gm-empty-icon" aria-hidden="true">✦</span>
          <p>ยังไม่มีตัวจัดการ Event</p>
          <p class="hint">กำหนดช่วงเวลา โบนัส EXP และผูกฉากจาก home editor</p>
        </div>
      `;
    }
    return `
      <div class="gm-empty-state">
        <span class="gm-empty-icon" aria-hidden="true">◇</span>
        <p>ยังไม่มีตาราง Drop</p>
        <p class="hint">ควรสุ่มรางวัลฝั่ง server แล้ว sync ลง client</p>
      </div>
    `;
  };

  const renderMain = () => {
    const meta = SECTION_META[section];
    const main = root.querySelector<HTMLElement>(".gm-main");
    const title = root.querySelector<HTMLElement>(".gm-page-title");
    const subtitle = root.querySelector<HTMLElement>(".gm-page-subtitle");
    const crumb = root.querySelector<HTMLElement>(".gm-crumb-current");
    if (!main || !title || !subtitle) return;
    title.textContent = meta.title;
    subtitle.textContent = meta.subtitle;
    if (crumb) crumb.textContent = meta.label;
    unbindSettings?.();
    unbindSettings = null;
    unbindUsers?.();
    unbindUsers = null;
    unbindMonsters?.();
    unbindMonsters = null;
    unbindParts?.();
    unbindParts = null;
    unbindTower?.();
    unbindTower = null;
    unbindShop?.();
    unbindShop = null;
    unbindControl?.();
    unbindControl = null;
    unbindRankBadges?.();
    unbindRankBadges = null;
    unbindHomeRail?.();
    unbindHomeRail = null;
    main.innerHTML = renderSectionBody();
    if (section === "overview") {
      const syncBtn = root.querySelector<HTMLButtonElement>("#gm-sync-claims-btn");
      const syncStatus = root.querySelector<HTMLElement>("#gm-sync-claims-status");
      const onSyncClaims = () => {
        if (!syncBtn) return;
        syncBtn.disabled = true;
        if (syncStatus) syncStatus.textContent = "กำลังซิงค์…";
        void (async () => {
          try {
            const nextClaims = await syncGmClaimsNow(user);
            const nextAccess = resolveGmAccess(user, nextClaims);
            if (syncStatus) {
              syncStatus.textContent = nextAccess.verifiedByClaims
                ? "ยืนยัน owner แล้ว — ลองบันทึกอีกครั้ง"
                : "server ยังไม่ให้ owner — ตั้ง GM_OWNER_EMAIL / config/gmAccess แล้ว deploy functions";
            }
          } catch {
            if (syncStatus) syncStatus.textContent = "ซิงค์ไม่สำเร็จ";
          } finally {
            syncBtn.disabled = false;
          }
        })();
      };
      syncBtn?.addEventListener("click", onSyncClaims);
      unbindSettings = () => syncBtn?.removeEventListener("click", onSyncClaims);
    }
    if (section === "settings") {
      unbindSettings = bindGmSettingsSection(root);
    }
    if (section === "monsters") {
      unbindMonsters = bindGmMonstersSection(root, user.uid);
    }
    if (section === "parts") {
      unbindParts = bindGmPartsSection(root, user.uid);
    }
    if (section === "shop") {
      unbindShop = bindGmShopSection(root);
    }
    if (section === "tower") {
      unbindTower = bindGmTowerSection(root);
    }
    if (section === "users") {
      unbindUsers = bindGmUsersSection(root);
    }
    if (section === "rankBadges") {
      unbindRankBadges = bindGmRankBadgesSection(root, user, rankBadgeDrafts);
    }
    if (section === "dungeonShop") {
      bindGmDungeonShopSection(root, user, dungeonShopDraft);
    }
    if (section === "homeRail") {
      unbindHomeRail = bindGmHomeRailSection(root);
    }
    if (section === "control") {
      unbindControl = bindGmControlSection(root, user);
    }
    for (const el of root.querySelectorAll<HTMLElement>("[data-section]")) {
      const id = el.dataset.section as GmSection | undefined;
      if (!id) continue;
      if (el.classList.contains("gm-nav-item")) {
        el.classList.toggle("gm-nav-item--active", id === section);
        el.toggleAttribute("aria-current", id === section);
      }
      el.classList.toggle("gm-shortcut--active", id === section && el.classList.contains("gm-shortcut"));
    }
  };

  const navigateTo = (next: GmSection, mode: "push" | "replace" = "push") => {
    section = next;
    const path = gmPathForSection(next);
    if (normalizeAppPath(window.location.pathname) !== path) {
      if (mode === "replace") history.replaceState({ gmSection: next }, "", path);
      else history.pushState({ gmSection: next }, "", path);
    }
    renderMain();
  };

  const primaryNav = PRIMARY_SECTIONS.map((id) => navLink(id, section)).join("");
  const laterNav = LATER_SECTIONS.map((id) => navLink(id, section)).join("");

  root.innerHTML = `
    <div class="gm-layout">
      <aside class="gm-sidebar" aria-label="เมนู GM">
        <div class="gm-sidebar-brand">
          <span class="gm-sidebar-logo" aria-hidden="true">GM</span>
          <div>
            <p class="gm-sidebar-app">Mind Survival</p>
            <p class="gm-sidebar-tag">Operator Console</p>
          </div>
        </div>

        <div class="gm-nav-group">
          <p class="gm-nav-group-label">งานหลัก</p>
          <nav class="gm-sidebar-nav" aria-label="งานหลัก">${primaryNav}</nav>
        </div>

        <div class="gm-nav-group">
          <p class="gm-nav-group-label">ระบบถัดไป</p>
          <nav class="gm-sidebar-nav" aria-label="ระบบถัดไป">${laterNav}</nav>
        </div>

        <div class="gm-sidebar-footer">
          <p class="gm-sidebar-user" title="${escapeHtml(email)}">${escapeHtml(email)}</p>
          <div class="gm-sidebar-links">
            <a class="gm-side-link" href="/home?character=1">ตัวละคร</a>
            <a class="gm-side-link" href="/home">บ้าน / เล่น</a>
            <button type="button" class="gm-side-link" id="gm-sign-out">ออกจากระบบ</button>
          </div>
        </div>
      </aside>

      <div class="gm-content">
        <header class="gm-content-header">
          <p class="gm-crumb"><span>GM</span><span aria-hidden="true">/</span><span class="gm-crumb-current">${SECTION_META[section].label}</span></p>
          <h1 class="gm-page-title">${SECTION_META[section].title}</h1>
          <p class="gm-page-subtitle">${SECTION_META[section].subtitle}</p>
        </header>
        <main class="gm-main">${renderSectionBody()}</main>
      </div>
    </div>
  `;

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "gm-sign-out" || target.closest("#gm-sign-out")) {
      void signOutUser();
      return;
    }
    const link = target.closest<HTMLAnchorElement>("a[data-section]");
    if (!link?.dataset.section) return;
    event.preventDefault();
    const next = link.dataset.section as GmSection;
    navigateTo(next);
  };

  const onPopState = () => {
    navigateTo(parseGmSectionFromPath(window.location.pathname), "replace");
  };

  root.addEventListener("click", onClick);
  window.addEventListener("popstate", onPopState);
  renderMain();

  return () => {
    unbindSettings?.();
    unbindUsers?.();
    unbindMonsters?.();
    unbindParts?.();
    unbindTower?.();
    unbindShop?.();
    unbindControl?.();
    unbindRankBadges?.();
    unbindHomeRail?.();
    root.removeEventListener("click", onClick);
    window.removeEventListener("popstate", onPopState);
    root.innerHTML = "";
  };
}
